import React, { useCallback, useEffect, useState } from "react";
import * as z from "zod";
import isEqual from "lodash/isEqual";

/**
 * Custom serializer/deserializer pair for URL state encoding. When provided, replaces the default JSON serialization
 * with a custom format (e.g., comma-separated keys for set-like state). The Zod schema still validates the deserialized
 * output, so the deserializer can return `unknown` safely.
 */
export interface UrlSerializer<T> {
  /** Serialize state to a URL-safe string. */
  serialize: (state: T) => string;
  /** Deserialize a URL parameter string back to state. Validated by Zod schema after deserialization. */
  deserialize: (encoded: string) => unknown;
}

/**
 * Options for the usePersistedState hook.
 */
export interface UsePersistedStateOptions<T> {
  /** Globally unique key for localStorage persistence. */
  componentId: string;

  /**
   * (Optional) Key to use for the URL query parameter. Only needs to be unique for the current page. Defaults to
   * `componentId` if not provided.
   */
  urlKey?: string;

  /** Zod schema for the state being persisted. */
  stateSchema: z.ZodSchema<T>;

  /** Schema version number for migration/invalidation. */
  version: number;

  /** Default state if nothing is found in storage or URL. */
  defaultState: T;

  /**
   * (Optional) Custom serializer for URL state. When omitted, state is JSON-stringified. Provide this to produce
   * shorter, more readable URL parameters for state shapes that support a simpler encoding.
   */
  urlSerializer?: UrlSerializer<T>;
}

/**
 * Callbacks and status flags exposed by usePersistedState for managing persisted state.
 */
export interface UsePersistedStateActions {
  saveState: () => void;
  resetToSaved: () => void;
  resetToDefaults: () => void;
  stateMatchesSaved: boolean;
  stateMatchesDefault: boolean;
  hasSavedState: boolean;
}

/**
 * State persisted in localStorage or URL, with versioning.
 */
interface PersistedState<T> {
  version: number;
  state: T;
}

/**
 * React hook for persisted state with URL and localStorage, Zod validation, and versioning.
 * Returns [state, setState, actions].
 */
export function usePersistedState<T>({
  componentId,
  urlKey,
  stateSchema,
  version,
  defaultState,
  urlSerializer,
}: UsePersistedStateOptions<T>): [T, React.Dispatch<React.SetStateAction<T>>, UsePersistedStateActions] {
  // Use urlKey if provided, otherwise fall back to componentId
  const urlParamKey = urlKey || componentId;

  // Construct the persisted state schema dynamically
  const persistedSchema = z.object({
    version: z.literal(version),
    state: stateSchema,
  }) as z.ZodSchema<PersistedState<T>>;

  const [state, setState] = useState<T>(() =>
    loadState(componentId, urlParamKey, stateSchema, persistedSchema, version, defaultState, urlSerializer),
  );

  // Track the saved state from localStorage as a separate state object
  const [savedState, setSavedState] = useState<T | undefined>(() =>
    getLocalStorageState(componentId, persistedSchema, version),
  );

  // Check for equality between the current state, the saved state, and the default state
  const stateMatchesDefault = isEqual(state, defaultState);
  const stateMatchesSaved = (stateMatchesDefault && !savedState) || isEqual(state, savedState);

  // Sync state to URL on state changes; if the state is equal to the default state, remove the URL param
  useEffect(() => {
    if (stateMatchesDefault) {
      resetUrlState(urlParamKey);
    } else {
      saveUrlState(urlParamKey, state, urlSerializer);
    }
  }, [stateMatchesDefault, urlParamKey, state, urlSerializer]);

  // Define 'save state' callback (save state to localStorage)
  const saveState = useCallback(() => {
    if (stateMatchesDefault) {
      resetLocalStorageState(componentId);
      setSavedState(undefined);
    } else {
      saveLocalStorageState(componentId, state, version);
      setSavedState(state);
    }
  }, [stateMatchesDefault, componentId, state, version]);

  // Define 'reset to saved' callback (reset state to what is stored in localStorage)
  const resetToSaved = useCallback(() => {
    if (!savedState) throw new Error("Cannot reset to saved state, no saved state found");
    setState(savedState);
    saveUrlState(urlParamKey, savedState, urlSerializer);
  }, [urlParamKey, savedState, urlSerializer]);

  // Define 'reset to defaults' callback
  const resetToDefaults = useCallback(() => {
    setState(defaultState);
    saveUrlState(urlParamKey, defaultState, urlSerializer);
  }, [urlParamKey, defaultState, urlSerializer]);

  return [
    state,
    setState,
    {
      saveState,
      resetToSaved,
      resetToDefaults,
      stateMatchesSaved,
      stateMatchesDefault,
      hasSavedState: savedState !== undefined,
    },
  ];
}

/**
 * Renders Save/Reset buttons for persisted state with correct conditional visibility.
 */
export const PersistedStateActionButtons: React.FC<UsePersistedStateActions> = ({
  saveState,
  resetToSaved,
  resetToDefaults,
  stateMatchesSaved,
  stateMatchesDefault,
  hasSavedState,
}) => (
  <>
    <button className="btn btn-primary btn-sm ms-2" onClick={saveState} type="button" disabled={stateMatchesSaved}>
      Save Filter
    </button>
    {hasSavedState && !stateMatchesSaved && (
      <button className="btn btn-secondary btn-sm ms-2" onClick={resetToSaved} type="button">
        Reset to Saved
      </button>
    )}
    {!stateMatchesDefault && (!hasSavedState || stateMatchesSaved) && (
      <button className="btn btn-secondary btn-sm ms-2" onClick={resetToDefaults} type="button">
        Reset to Defaults
      </button>
    )}
  </>
);

/**
 * Load state from URL or localStorage, with Zod validation and versioning.
 */
function loadState<T>(
  componentId: string,
  urlParamKey: string,
  stateSchema: z.ZodSchema<T>,
  persistedSchema: z.ZodSchema<PersistedState<T>>,
  version: number,
  defaultState: T,
  urlSerializer?: UrlSerializer<T>,
): T {
  // Try URL first
  const urlState = getUrlState<T>(urlParamKey, stateSchema, urlSerializer);
  if (urlState !== undefined) return urlState;

  // Try localStorage
  const localStorageState = getLocalStorageState<T>(componentId, persistedSchema, version);
  if (localStorageState !== undefined) return localStorageState;

  // Fallback to default state
  return defaultState;
}

function getLocalStorageState<T>(
  componentId: string,
  schema: z.ZodSchema<PersistedState<T>>,
  version: number,
): T | undefined {
  const raw = localStorage.getItem(componentId);
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      const result = schema.safeParse(parsed);
      if (result.success && result.data.version === version) {
        return result.data.state;
      }
    } catch (e) {
      // Ignore parse errors and return undefined
      console.warn(`Failed to parse persistent state from local storage for '${componentId}':`, e);
    }
  }
  return undefined;
}

/**
 * Save state to localStorage with version.
 */
function saveLocalStorageState(componentId: string, state: unknown, version: number) {
  const toSave: PersistedState<unknown> = { version, state };
  localStorage.setItem(componentId, JSON.stringify(toSave));
  console.info(`Saved state to localStorage for '${componentId}':`, toSave);
}

/**
 * Remove state from localStorage.
 */
function resetLocalStorageState(componentId: string) {
  localStorage.removeItem(componentId);
  console.info(`Removed state from localStorage for '${componentId}'`);
}

/**
 * Extract and validate state from URL.
 *
 * URL state is stored without the version wrapper — only the raw state is serialized. The Zod schema alone handles
 * validation/invalidation of stale URL state (rejecting payloads whose shape no longer matches). This keeps URLs
 * short and readable. The tradeoff is that bumping `version` alone (without a schema shape change) won't invalidate
 * URL state the way it does for localStorage.
 */
function getUrlState<T>(paramKey: string, schema: z.ZodSchema<T>, urlSerializer?: UrlSerializer<T>): T | undefined {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get(paramKey);
  if (!encoded) return undefined;
  try {
    // Deserialize using custom serializer or default JSON parsing
    const parsed: unknown = urlSerializer ? urlSerializer.deserialize(encoded) : JSON.parse(encoded);
    const result = schema.safeParse(parsed);
    if (result.success) {
      return result.data;
    }
  } catch (e) {
    // Ignore parse errors and return undefined
    console.warn(`Failed to parse persistent state from URL for '${paramKey}':`, e);
  }
  return undefined;
}

/**
 * Save state to URL.
 *
 * Only the raw state is saved (no version wrapper) to keep URLs short and readable. Zod schema validation is
 * sufficient to reject stale URL state when the shape changes. See {@link getUrlState} for the full rationale.
 *
 * Uses the custom {@link UrlSerializer} if provided, otherwise falls back to JSON stringification.
 */
function saveUrlState<T>(paramKey: string, state: T, urlSerializer?: UrlSerializer<T>) {
  const params = new URLSearchParams(window.location.search);
  // Serialize using custom serializer or default JSON stringification
  params.set(paramKey, urlSerializer ? urlSerializer.serialize(state) : JSON.stringify(state));

  // URLSearchParams uses application/x-www-form-urlencoded encoding, which percent-encodes many characters that are
  // actually valid in query strings per RFC 3986. We unescape commas and colons because:
  //   - Both are explicitly allowed in the RFC 3986 query grammar (`,` as a sub-delim, `:` as part of pchar)
  //   - URLSearchParams.get() returns them correctly on the read path (it only splits on `&` and `=`)
  //   - They're common in our serialized data (commas in CSV-style custom serializers, colons in JSON key-value pairs)
  // Other characters like ~ ! $ ' ( ) @ / ? are also RFC 3986 query-safe and could be unescaped here if needed, but
  // they rarely appear in our serialized state so the readability benefit is negligible.
  const queryString = params.toString().replaceAll("%2C", ",").replaceAll("%3A", ":");
  const newUrl = params.size ? `${window.location.pathname}?${queryString}` : window.location.pathname;
  window.history.replaceState({}, "", newUrl);
  console.info(`Saved state to URL for '${paramKey}':`, state);
}

/** Remove state from URL. */
function resetUrlState(paramKey: string) {
  // Do nothing if the paramKey is not present in the URL query parameter
  const url = new URL(window.location.href);
  if (!url.searchParams.has(paramKey)) return;

  // Otherwise, remove the parameter and update the URL without affecting the browser history
  url.searchParams.delete(paramKey);
  window.history.replaceState({}, "", url);
  console.info(`Removed state from URL for '${paramKey}'`);
}
